import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { EnvelopeSimple, Handshake, Megaphone, Lightning, PaperPlaneTilt } from "@phosphor-icons/react";
import { PageHeader } from "../components/PageHeader";
import { BUSINESS_EMAIL } from "../config/socials";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const reasons = [
    { icon: Handshake, label: "Collabs", desc: "Stream collabs, charity events, community crossovers." },
    { icon: Megaphone, label: "Sponsorships", desc: "Brand deals & creator partnerships. Send the deck." },
    { icon: Lightning, label: "Press / Media", desc: "Interviews, features, podcasts. Hit me up." },
];

export const ContactPage = () => {
    const [form, setForm] = useState({ name: "", email: "", topic: "Collabs", message: "" });
    const [sending, setSending] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
            toast.error("Fill out name, email and message first.");
            return;
        }
        setSending(true);
        try {
            await axios.post(`${API}/contact`, form);
            toast.success("Message sent — I'll get back to you between streams.", {
                description: `Re: ${form.topic}`,
            });
            setForm({ name: "", email: "", topic: "Collabs", message: "" });
        } catch (err) {
            const msg = err?.response?.data?.detail || "Couldn't send. Try again or email me directly.";
            toast.error(String(msg));
        } finally {
            setSending(false);
        }
    };

    return (
        <div data-testid="contact-page">
            <PageHeader
                eyebrow="Get in touch"
                title="Wanna work"
                accent="together?"
                intro="Collabs, sponsorships, press — everything that isn't 'mod me' goes here. For mod inquiries, hop in the Discord."
                breadcrumbs={[{ label: "Home", href: "/" }, { label: "Contact" }]}
            />

            <section className="relative py-10 lg:py-14">
                <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-5 gap-6">
                    {/* Reasons */}
                    <div className="lg:col-span-2 space-y-3">
                        {reasons.map((r, i) => (
                            <motion.div
                                key={r.label}
                                initial={{ opacity: 0, x: -12 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.4, delay: i * 0.07 }}
                                className="rounded-2xl border border-white/10 bg-obsidian-900/60 p-5 flex gap-4 card-tilt"
                                data-testid={`contact-reason-${i}`}
                            >
                                <div className="h-11 w-11 shrink-0 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
                                    <r.icon size={18} className="text-brand-cyan" weight="fill" />
                                </div>
                                <div>
                                    <div className="font-display text-base text-white">{r.label}</div>
                                    <p className="text-sm text-white/55 mt-1 leading-relaxed">{r.desc}</p>
                                </div>
                            </motion.div>
                        ))}

                        <motion.a
                            href={`mailto:${BUSINESS_EMAIL}`}
                            initial={{ opacity: 0, x: -12 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.4, delay: 0.25 }}
                            className="block rounded-2xl border border-white/10 bg-obsidian-900/80 p-5 hover:border-brand-cyan/30 transition"
                            data-testid="contact-email-direct"
                        >
                            <div className="flex items-center gap-2 mb-2">
                                <EnvelopeSimple size={15} className="text-brand-purple" weight="fill" />
                                <span className="eyebrow">Direct</span>
                            </div>
                            <div className="font-mono text-white text-sm sm:text-base break-all">{BUSINESS_EMAIL}</div>
                            <p className="text-xs text-white/45 mt-2 leading-relaxed">
                                Drop it straight in the inbox — easiest if you've already got a deck or proposal ready.
                            </p>
                        </motion.a>
                    </div>

                    {/* Form */}
                    <motion.form
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                        onSubmit={onSubmit}
                        className="lg:col-span-3 rounded-2xl border border-white/10 bg-obsidian-900/60 p-7"
                        data-testid="contact-form"
                    >
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                            <div>
                                <label className="eyebrow">Name</label>
                                <input
                                    type="text"
                                    value={form.name}
                                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    placeholder="Your name / handle"
                                    className="mt-2 w-full bg-black/40 border border-white/10 rounded-lg px-3.5 py-2.5 text-white text-sm placeholder-white/25 focus:border-brand-cyan/60 focus:outline-none focus:ring-2 focus:ring-brand-cyan/15 transition"
                                    data-testid="contact-input-name"
                                />
                            </div>
                            <div>
                                <label className="eyebrow">Email</label>
                                <input
                                    type="email"
                                    value={form.email}
                                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                                    placeholder="you@brand.com"
                                    className="mt-2 w-full bg-black/40 border border-white/10 rounded-lg px-3.5 py-2.5 text-white text-sm placeholder-white/25 focus:border-brand-cyan/60 focus:outline-none focus:ring-2 focus:ring-brand-cyan/15 transition"
                                    data-testid="contact-input-email"
                                />
                            </div>
                        </div>
                        <div className="mb-4">
                            <label className="eyebrow">Topic</label>
                            <div className="mt-2 flex flex-wrap gap-2" data-testid="contact-topic-pills">
                                {["Collabs", "Sponsorships", "Press / Media", "Other"].map((t) => (
                                    <button
                                        key={t}
                                        type="button"
                                        onClick={() => setForm({ ...form, topic: t })}
                                        className={`px-3.5 py-1.5 rounded-full text-xs border transition ${
                                            form.topic === t
                                                ? "bg-white text-black border-white"
                                                : "bg-white/5 text-white/65 border-white/10 hover:border-white/30"
                                        }`}
                                        data-testid={`contact-topic-${t.toLowerCase().replace(/[\s/]+/g, "-")}`}
                                    >
                                        {t}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="mb-6">
                            <label className="eyebrow">Message</label>
                            <textarea
                                rows={5}
                                value={form.message}
                                onChange={(e) => setForm({ ...form, message: e.target.value })}
                                placeholder="What's the move?"
                                className="mt-2 w-full bg-black/40 border border-white/10 rounded-lg px-3.5 py-2.5 text-white text-sm placeholder-white/25 focus:border-brand-cyan/60 focus:outline-none focus:ring-2 focus:ring-brand-cyan/15 transition resize-none"
                                data-testid="contact-input-message"
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={sending}
                            className="btn-sweep w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-white text-black text-sm font-semibold hover:bg-brand-cyan transition disabled:opacity-60"
                            data-testid="contact-submit-btn"
                        >
                            <PaperPlaneTilt size={15} weight="fill" />
                            {sending ? "Sending..." : "Send Message"}
                        </button>
                        <p className="text-[11px] text-white/35 mt-4">
                            By submitting you accept the obvious: I'll only use this to reply to you. No spam, no resell.
                        </p>
                    </motion.form>
                </div>
            </section>
        </div>
    );
};
